import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Container,
  Stack,
  Divider,
} from '@mui/material';

import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';

import { getCosts, getUserCredits } from '../../api/credits';
import { getUser } from '../../api/users';
import { createPaymentSession } from '../../api/payments';
import { loadStripe } from '@stripe/stripe-js';

// ---- Tipi -------------------------------------------------------------
interface User {
  id_utente: number;
  nome: string;
  cognome: string;
  codice_fiscale: string;
  data_nascita: string;
  email: string;
}

interface Cost {
  id_costo: number;
  id_tipo_utente: number;
  id_servizio: number;
  quantita: number;         // n° crediti
  costo: number;            // in centesimi
  valuta: string;           // EUR...
  api_prezzo_stripe: string;
}

// ---- Helpers / Stili --------------------------------------------------
const stripePromise = loadStripe(
  'pk_live_51JYfm3F8FajHpf3Qy3iu4rTSRpmEgkCXmwbjCHYzPJsDiFqxZwncQ22PIS353Z32pw4sfcbmZ1h02bWjbAjyUmOZ00N8wDT1hg'
); // chiave pubblicabile

const cardSx = {
  bgcolor: 'background.paper',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none',
  height: '100%',
};

const fmtEUR = (cents: number, currency = 'EUR') =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format((cents || 0) / 100);

// ----------------------------------------------------------------------

const ViewCredit: React.FC = () => {
  const [offers, setOffers] = useState<Cost[]>([]);
  const [userCredits, setUserCredits] = useState<number>(0);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // fetch iniziale
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userIdFromStorage = localStorage.getItem('userId');
        if (!userIdFromStorage) {
          throw new Error('User ID not found in storage');
        }

        const userId = parseInt(userIdFromStorage, 10);
        const user = await getUser(userId);
        setUser(user);

        const credits = await getUserCredits(user.id_utente);
        setUserCredits(credits);
      } catch (error) {
        console.error('Error fetching user data or credits:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchOffers = async () => {
      try {
        const data = await getCosts();
        // Mostra solo le offerte per l’utente “finale” (id_tipo_utente = 3)
        const filteredOffers = data.filter((offer: Cost) => offer.id_tipo_utente === 3);
        setOffers(filteredOffers);
      } catch (error) {
        console.error('Error fetching offers:', error);
      }
    };

    fetchUserData();
    fetchOffers();
  }, []);

  const handlePurchase = async (cost: Cost) => {
    try {
      const success_url = window.location.origin + '/payment-success';
      const cancel_url = window.location.origin + '/payment-cancel';

      const paymentResponse = await createPaymentSession(cost.id_costo, success_url, cancel_url);
      const stripe = await stripePromise;
      const { error } = await stripe!.redirectToCheckout({ sessionId: paymentResponse.id_sessione });

      if (error) console.error('Stripe checkout error:', error);
    } catch (error) {
      console.error('Error processing payment:', error);
    }
  };

  // --------------------------------------------------------------------

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <Typography variant="h6" color="white">Caricamento…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: 'calc(100vh - 64px)' }}>
      {/* Titolo + sottotitolo */}
      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2, pb: 1 }}>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800 }}>
          Portafoglio
        </Typography>
        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
          Visualizza il saldo dei crediti e acquista pacchetti aggiuntivi.
        </Typography>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 4 }}>
        {/* Sezione 1: saldo */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
            Saldo disponibile
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} lg={3}>
              <Card sx={{ ...cardSx, p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1">Crediti residui</Typography>
                  <Box
                    sx={{
                      width: 40, height: 40, borderRadius: '50%',
                      border: '1px solid', borderColor: 'divider',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    <MonetizationOnIcon />
                  </Box>
                </Stack>
                <Typography variant="h4" fontWeight={800}>
                  {userCredits.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Crediti utilizzabili per i servizi attivi
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Sezione 2: offerte disponibili */}
        <Box sx={{ mb: 1, mt: 6 }}>
          <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
            Offerte disponibili
          </Typography>
          <Grid container spacing={2}>
            {offers.map((offer) => (
              <Grid key={offer.id_costo} item xs={12} sm={6} md={4} lg={3}>
                <Card sx={{ ...cardSx, p: 2 }}>
                  <Stack spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>
                      {offer.quantita} crediti
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ricarica il tuo portafoglio per usare i servizi disponibili.
                    </Typography>
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <AddShoppingCartIcon color="action" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Prezzo
                    </Typography>
                  </Stack>

                  <Typography variant="h5" fontWeight={800} color="primary" sx={{ mb: 1 }}>
                    {fmtEUR(offer.costo, offer.valuta?.toUpperCase() || 'EUR')}
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => handlePurchase(offer)}
                  >
                    Acquista ora
                  </Button>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default ViewCredit;
